import { useCallback, useEffect, useRef, useState } from 'react';
import type { BatchItem, Card, ItemKind } from '../types';
import { fetchCards, type Queue } from '../lib/api';
import { LOCALE_META } from '../lib/locales';
import PromptCard from '../components/PromptCard';

const PAGE = 25;

const COPY: Record<Queue, { title: string; lede: string; empty: string }> = {
  flagged: {
    title: 'Known problems',
    lede: 'Defects the automatic checker found in your languages — a broken placeholder, a player count that disagrees with the English, or a string that was never translated. The highest-value place to start.',
    empty: 'No flagged problems in your languages. That is the checker saying these decks are clean.',
  },
  unread: {
    title: 'Needs review',
    lede: 'Translations that pass every automatic check but no human has read yet. This is where awkward phrasing and wrong register get caught — the part only a fluent speaker can do.',
    empty: 'Nothing left unread here.',
  },
  reviewed: {
    title: 'Already checked',
    lede: 'Strings a reviewer has confirmed read correctly.',
    empty: 'Nothing has been marked good yet.',
  },
};

export default function Review({
  locales, queue, meId, decisions, onDecide, onUndo, showHow, onHideHow,
}: {
  locales: string[];
  queue: Queue;
  meId: string;
  decisions: Map<string, BatchItem[]>;
  onDecide: (card: Card, kind: ItemKind, payload: { values?: Map<string, string>; note?: string }) => void;
  onUndo: (card: Card) => void;
  showHow: boolean;
  onHideHow: (v: boolean) => void;
}) {
  const [cards, setCards] = useState<Card[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [only, setOnly] = useState<string | null>(null);
  // Bumped when someone posts into a thread, to pull the page back in with the
  // new note. Cheap, and it keeps the card the single source of its own thread.
  const [threads, setThreads] = useState(0);

  // Enter on the last field of a card saves it and moves to the next one still
  // waiting, so a fluent reviewer can work the queue without touching a mouse.
  // Cards register themselves and drop out once decided.
  const focusers = useRef(new Map<string, () => void>());
  const registerFocus = useCallback((key: string, focus: (() => void) | null) => {
    if (focus) focusers.current.set(key, focus);
    else focusers.current.delete(key);
  }, []);

  const scope = only ? [only] : locales;

  useEffect(() => { setPage(0); }, [queue, only]);

  useEffect(() => {
    let alive = true;
    setLoading(true); setError(null);
    fetchCards(scope, queue, { limit: PAGE, offset: page * PAGE })
      .then((r) => { if (alive) setCards(r); })
      .catch((e) => { if (alive) setError(e.message ?? String(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queue, page, only, threads, locales.join(',')]);

  // After a paint: the card that was just saved has to re-render into its done
  // strip and unregister before we can tell which one is genuinely next.
  const advance = useCallback((fromKey: string) => {
    const order = cards.map((c) => c.key);
    const from = order.indexOf(fromKey);
    requestAnimationFrame(() => {
      for (let i = from + 1; i < order.length; i++) {
        const focus = focusers.current.get(order[i]);
        if (focus) { focus(); return; }
      }
    });
  }, [cards]);

  const copy = COPY[queue];

  return (
    <>
      <div className="phead">
        <div>
          <h1>{copy.title}</h1>
          <p>{copy.lede}</p>
        </div>
        {!showHow && (
          <div className="acts">
            <button className="btn sm" onClick={() => onHideHow(false)}>What do the buttons do?</button>
          </div>
        )}
      </div>

      {showHow && (
        <div className="howto">
          <div className="hd">What each action does
            <button className="close" onClick={() => onHideHow(true)}>Hide</button>
          </div>
          <div className="ln"><span className="pill p-acc">Save and confirm</span>
            <span>The one action for both cases. Correct anything that reads wrong by typing straight
              into the right-hand box, then save. Text you did not touch is recorded as checked and
              correct, so reading a card through is never wasted.</span></div>
          <div className="ln"><span className="pill p-warn">Ask Sam</span>
            <span>Something is off but you cannot fix it — ambiguous English, a cultural problem, a term
              you are unsure of. Attaches a question to the prompt, and his answer comes back in the
              same place.</span></div>
          <div className="ln"><span className="pill p-mute">Notes</span>
            <span>The conversation about a prompt, kept with the prompt rather than with whoever asked.
              Anything already asked and answered is there, so you are not the second person to wonder
              the same thing — and you can answer a question yourself if you know it.</span></div>
          <div className="ln"><span className="pill p-mute">Keyboard</span>
            <span><b>Tab</b> moves between the boxes. <b>Enter</b> jumps from the question to its
              instruction, and from the instruction it saves the card and drops you into the next
              one — so a whole page can be walked without reaching for the mouse.
              <b>Shift+Enter</b> puts in a line break.</span></div>
          <div className="ln"><span className="pill p-mute">All three</span>
            <span>Land in <b>My batch</b> first. You see the whole list and can pull anything out before
              you send it. <b>Nothing leaves this page until then.</b></span></div>
        </div>
      )}

      {locales.length > 1 && (
        <div className="filters">
          <button className="chip" aria-pressed={!only} onClick={() => setOnly(null)}>All</button>
          {locales.map((l) => (
            <button key={l} className="chip" aria-pressed={only === l} onClick={() => setOnly(l)}>
              {LOCALE_META[l]?.native ?? l}
            </button>
          ))}
        </div>
      )}

      {error && (
        <div className="panel"><div className="empty">
          <b>Could not load</b>{error}
        </div></div>
      )}

      {loading && !error && <div className="panel"><div className="empty">Loading…</div></div>}

      {!loading && !error && cards.length === 0 && (
        <div className="panel"><div className="empty"><b>Nothing here</b>{copy.empty}</div></div>
      )}

      {!loading && !error && cards.length > 0 && (
        <>
          <div className="rows">
            {cards.map((c) => (
              <PromptCard
                key={c.key}
                card={c}
                decided={decisions.get(c.key)}
                meId={meId}
                onDecide={onDecide}
                onUndo={onUndo}
                onNotesChanged={() => setThreads((n) => n + 1)}
                onRegisterFocus={registerFocus}
                onAdvance={advance}
              />
            ))}
          </div>
          <div className="filters" style={{ marginTop: 14 }}>
            <button className="btn sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
              ← Previous
            </button>
            <span className="hint">Page {page + 1}</span>
            <button className="btn sm" disabled={cards.length < PAGE} onClick={() => setPage((p) => p + 1)}>
              Next →
            </button>
          </div>
        </>
      )}
    </>
  );
}
